import { useState, type ReactNode, type SVGProps } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '../lib/cn';

// =============================================================================
// PageScaffold — the enterprise application frame
// =============================================================================
// Every in-app page renders through this. It pins the page header (title +
// actions) and an optional footer (form actions), and gives the page EXACTLY
// ONE scroll region — so on desktop the page chrome never moves and there is no
// window scroll. Below `lg` it relaxes to normal document flow (AppShell's
// <main> scrolls) so touch + on-screen keyboards behave.
//
//   scroll (default true)  — content area is the scroll region (detail pages,
//                            dashboards, forms). Children flow with `space-y-6`.
//   scroll={false}         — content area is a height-bounded flex column; the
//                            page supplies its own contained scroller, typically
//                            <DataTableCard>. Used by list/table pages so only
//                            the table body scrolls. Children get `gap-4`.
//
// Width is capped at max-w-7xl by default to match the previous AppShell.

type Width = 'narrow' | 'form' | 'form-wide' | 'default' | 'wide' | 'full';

// Header and content share the same max-width so they stay aligned. Because the
// project's `cn` is plain clsx (no tailwind-merge), width MUST be chosen here via
// this prop — a max-w-* passed through contentClassName would collide with the
// default and not reliably win.
const WIDTHS: Record<Width, string> = {
  narrow: 'max-w-3xl',
  form: 'max-w-4xl',
  'form-wide': 'max-w-5xl',
  default: 'max-w-7xl',
  wide: 'max-w-screen-2xl',
  full: 'max-w-none',
};

// Header utility controls (back / refresh). Mirrors IconButton's ghost style
// but at h-10 to line up with the header's action buttons (Button size "md").
const HEADER_ICON_BTN = cn(
  'inline-flex items-center justify-center shrink-0 h-10 w-10 rounded leading-none transition select-none',
  'bg-paper-2 border border-rule text-ink-3 hover:bg-paper hover:text-ink hover:border-rule-2',
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-soft',
);

function BackIcon(): JSX.Element {
  return (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  );
}

function RefreshIcon(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}
    >
      <polyline points="21.5 4.5 21.5 10 16 10" />
      <path d="M20.84 15a9 9 0 1 1-2.47-9.36L21.5 9" />
    </svg>
  );
}

interface Props {
  title?: ReactNode;
  subtitle?: ReactNode;
  /** Right-aligned header actions (primary/secondary buttons). */
  actions?: ReactNode;
  /** Explicit back target; when omitted the back button uses browser history. */
  back?: { to: string; label: ReactNode };
  /** Pinned footer bar — form/wizard actions live here, always reachable. */
  footer?: ReactNode;
  /** When true (default) the content area scrolls. Set false for contained tables. */
  scroll?: boolean;
  width?: Width;
  /** Overrides the default rhythm on the content wrapper. */
  contentClassName?: string;
  children: ReactNode;
}

export function PageScaffold({
  title,
  subtitle,
  actions,
  back,
  footer,
  scroll = true,
  width = 'default',
  contentClassName,
  children,
}: Props): JSX.Element {
  const max = WIDTHS[width];
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [reloading, setReloading] = useState(false);

  // The PWA runs without browser chrome, so the header carries the navigation
  // the browser would normally provide: a back button (hidden on the landing
  // page) and a refresh button. An explicit `back` target wins; otherwise we
  // walk browser history, falling back to home when the page was opened cold.
  const showBack = pathname !== '/';
  const backLabel = typeof back?.label === 'string' ? `Back to ${back.label}` : 'Back';
  const goBack = (): void => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate('/');
  };

  return (
    <div className="flex flex-col md:h-full md:overflow-hidden">
      {/* Header height is tuned to match the sidebar's logo bar (h-16) so the
          app's two top bars read as one continuous band. Compact title + tight
          padding keep it dense; the h-10 controls set the floor. */}
      <header className="shrink-0 sticky top-0 z-20 bg-paper border-b border-rule px-6 sm:px-8 py-2.5">
        <div className={cn('mx-auto w-full', max)}>
          <div className="flex items-center justify-between gap-4 flex-wrap min-h-[2.75rem]">
            <div className="flex items-center gap-3 min-w-0">
              {showBack &&
                (back ? (
                  <Link
                    to={back.to}
                    aria-label={backLabel}
                    title={backLabel}
                    className={HEADER_ICON_BTN}
                  >
                    <BackIcon />
                  </Link>
                ) : (
                  <button
                    type="button"
                    aria-label="Back"
                    title="Back"
                    onClick={goBack}
                    className={HEADER_ICON_BTN}
                  >
                    <BackIcon />
                  </button>
                ))}
              <div className="min-w-0">
                {title && (
                  <h1 className="kc-serif text-[1.75rem] leading-tight text-ink truncate">{title}</h1>
                )}
                {subtitle && <p className="text-sm text-ink-3 leading-snug mt-0.5">{subtitle}</p>}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                aria-label="Refresh page"
                title="Refresh page"
                onClick={() => {
                  setReloading(true);
                  window.location.reload();
                }}
                className={HEADER_ICON_BTN}
              >
                <RefreshIcon className={reloading ? 'animate-spin' : undefined} />
              </button>
              {actions}
            </div>
          </div>
        </div>
      </header>

      <div
        className={cn(
          scroll
            ? 'md:flex-1 md:min-h-0 md:overflow-y-auto'
            : 'md:flex-1 md:min-h-0 md:overflow-hidden flex flex-col',
        )}
      >
        <div
          className={cn(
            'mx-auto w-full px-6 sm:px-8 py-6',
            max,
            scroll ? 'space-y-6' : 'flex flex-col gap-4 md:flex-1 md:min-h-0',
            contentClassName,
          )}
        >
          {children}
        </div>
      </div>

      {footer && (
        <footer className="shrink-0 border-t border-rule bg-surface px-6 sm:px-8 py-3">
          <div className={cn('mx-auto w-full flex items-center justify-end gap-2', max)}>
            {footer}
          </div>
        </footer>
      )}
    </div>
  );
}
